import { z } from "zod";
import {
	AdminListInputSchema,
	adminItemOutput,
	adminListOutput,
} from "./common";

// =============
// Admin App Schemas
// =============

/**
 * Admin app schema - includes deletedAt for soft delete visibility.
 */
export const AdminAppSchema = z.object({
	id: z.string(),
	ownerId: z.string(),
	ownerEmail: z.string().nullish(), // Joined from user table
	name: z.string(),
	active: z.boolean(),
	testMode: z.boolean(),
	attributionMode: z.enum(["code", "window"]),
	attestationProvider: z.enum(["none", "firebase", "apple", "google"]),
	iapConfigs: z.unknown().nullable(),
	platforms: z.preprocess(
		(val) => (typeof val === "string" ? JSON.parse(val) : val),
		z.array(z.enum(["ios", "android", "any"])),
	),
	allowedCorsOrigins: z.string().nullable(),
	stripeAccountId: z.string().nullish(),
	createdAt: z.coerce.date(),
	archivedAt: z.coerce.date().nullable(),
	deletedAt: z.coerce.date().nullable(),
});
export type AdminApp = z.infer<typeof AdminAppSchema>;

/**
 * Admin app list input with app-specific filters.
 */
export const AdminAppListInputSchema = AdminListInputSchema.extend({
	filter: z
		.object({
			name: z.string().optional(),
			ownerId: z.string().optional(),
			active: z.coerce.boolean().optional(),
			testMode: z.coerce.boolean().optional(),
			platform: z.enum(["ios", "android", "any"]).optional(),
		})
		.optional(),
});
export type AdminAppListInput = z.infer<typeof AdminAppListInputSchema>;

/**
 * Admin app list output.
 */
export const AdminAppListOutputSchema = adminListOutput(AdminAppSchema);
export type AdminAppListOutput = z.infer<typeof AdminAppListOutputSchema>;

/**
 * Admin app single item output.
 */
export const AdminAppOutputSchema = adminItemOutput(AdminAppSchema);
export type AdminAppOutput = z.infer<typeof AdminAppOutputSchema>;

/**
 * Admin app create input.
 */
export const AdminAppCreateInputSchema = z.object({
	ownerId: z.string(),
	name: z.string().min(1),
	platforms: z.array(z.enum(["ios", "android", "any"])),
	active: z.boolean().optional(),
	testMode: z.boolean().optional(),
});
export type AdminAppCreateInput = z.infer<typeof AdminAppCreateInputSchema>;

/**
 * Admin app update input.
 */
export const AdminAppUpdateInputSchema = z.object({
	id: z.string(),
	name: z.string().min(1).optional(),
	active: z.boolean().optional(),
	testMode: z.boolean().optional(),
	platforms: z.array(z.enum(["ios", "android", "any"])).optional(),
	allowedCorsOrigins: z.string().nullable().optional(),
	attributionMode: z.enum(["code", "window"]).optional(),
	attestationProvider: z
		.enum(["none", "firebase", "apple", "google"])
		.optional(),
});
export type AdminAppUpdateInput = z.infer<typeof AdminAppUpdateInputSchema>;
